import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Checkbox } from '../ui/checkbox';
import {
  AlertTriangle, Check, Loader2, Paintbrush, Search, Info, HelpCircle, ChevronDown, ChevronRight, Plus
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================================
// LRV HELPER FUNCTIONS
// ============================================================================

const LRV_WARNING_THRESHOLD = 60;

/**
 * Safely convert a value to number or return null
 */
export const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const num = typeof value === 'number' ? value : parseFloat(value);
  return isNaN(num) ? null : num;
};

/**
 * Check if LRV is below the warning threshold
 */
export const isLowLRV = (lrvValue) => {
  const lrv = toNumberOrNull(lrvValue);
  return lrv !== null && lrv < LRV_WARNING_THRESHOLD;
};

/**
 * Calculate the effective LRV for an area
 */
export const getEffectiveLRV = (area, selectedColor) => {
  const manualLRV = toNumberOrNull(area?.lrv_value);
  if (manualLRV !== null) {
    return { value: manualLRV, source: 'manual' };
  }
  
  const colorLRV = toNumberOrNull(selectedColor?.lrv);
  if (colorLRV !== null) {
    return { value: colorLRV, source: 'color' };
  }
  
  return { value: null, source: 'missing' };
};

// ============================================================================
// COLOR SWATCH COMPONENT
// ============================================================================

const ColorSwatch = ({ hexCode, size = 'md' }) => {
  const sizeClasses = {
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-10 w-10'
  };
  
  if (!hexCode) {
    return (
      <div className={`${sizeClasses[size]} rounded border-2 border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center`}>
        <Paintbrush className="h-4 w-4 text-slate-400" />
      </div>
    );
  }
  
  return (
    <div 
      className={`${sizeClasses[size]} rounded border border-slate-200 dark:border-slate-700 shadow-sm`}
      style={{ backgroundColor: hexCode }}
      title={hexCode}
    />
  );
};

// ============================================================================
// COLOR SEARCH DROPDOWN COMPONENT
// ============================================================================

const ColorSearchDropdown = ({ value, onChange, colors, disabled, currentColor }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  
  const filteredColors = useMemo(() => {
    if (!searchTerm) return colors.slice(0, 50);
    const term = searchTerm.toLowerCase();
    return colors.filter(c => 
      c.color_name.toLowerCase().includes(term) ||
      (c.color_number && c.color_number.toLowerCase().includes(term))
    ).slice(0, 50);
  }, [colors, searchTerm]);
  
  const selectedColor = currentColor || colors.find(c => c.id === value);
  
  const handleSelectColor = (color) => {
    onChange(color);
    setIsOpen(false);
    setSearchTerm('');
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild disabled={disabled}>
        <div 
          className={`flex items-center gap-2 h-9 px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-blue-400 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
          role="button"
          tabIndex={disabled ? -1 : 0}
        >
          {selectedColor ? (
            <>
              <ColorSwatch hexCode={selectedColor.hex_code} size="sm" />
              <span className="text-sm truncate flex-1">{selectedColor.color_name}</span>
              <span className="text-xs text-slate-400">{selectedColor.color_number}</span>
            </>
          ) : (
            <span className="text-sm text-slate-400">Select color...</span>
          )}
        </div>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Select Paint Color</DialogTitle>
          <DialogDescription>Search Sherwin-Williams colors or add custom colors below</DialogDescription>
        </DialogHeader>
        
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by name or number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            autoFocus
          />
        </div>
        
        <div className="flex-1 overflow-y-auto mt-2 space-y-1 min-h-[200px] max-h-[400px]">
          {filteredColors.map(color => {
            const colorLRV = toNumberOrNull(color.lrv);
            const lowLRV = isLowLRV(colorLRV);
            
            return (
              <div
                key={color.id}
                className={`flex items-center gap-3 p-2 rounded-md cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 ${value === color.id ? 'bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500' : ''}`}
                onClick={() => handleSelectColor(color)}
              >
                <ColorSwatch hexCode={color.hex_code} size="md" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{color.color_name}</p>
                  <p className="text-xs text-slate-500">{color.color_number}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500">LRV</p>
                  <p className={`text-sm font-medium ${
                    colorLRV === null ? 'text-slate-400' :
                    lowLRV ? 'text-amber-600 dark:text-amber-400' : 
                    'text-green-600 dark:text-green-400'
                  }`}>
                    {colorLRV ?? '—'}
                  </p>
                </div>
              </div>
            );
          })}
          {filteredColors.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-4">No colors found</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// LRV DISPLAY COMPONENT (No restrictions, just display)
// ============================================================================

const LRVDisplay = ({ value, source }) => {
  const lrvValue = toNumberOrNull(value);
  const lowLRV = isLowLRV(lrvValue);
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`px-2 py-1 rounded text-center text-sm font-medium ${
            lrvValue === null ? 'text-slate-400 bg-slate-100 dark:bg-slate-800' :
            lowLRV ? 'text-amber-600 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400' : 
            'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400'
          }`}>
            {lrvValue ?? '—'}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            {source === 'manual' ? 'Manual entry' : 
             source === 'color' ? 'From color dataset' : 
             'No LRV value'}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// ============================================================================
// LRV MANUAL INPUT COMPONENT
// ============================================================================

const LRVInput = ({ value, onChange, disabled, source }) => {
  const [localValue, setLocalValue] = useState('');
  
  useEffect(() => {
    const numValue = toNumberOrNull(value);
    setLocalValue(numValue !== null ? numValue.toString() : '');
  }, [value]);
  
  const handleBlur = () => {
    const numValue = toNumberOrNull(localValue);
    if (numValue !== toNumberOrNull(value)) {
      onChange(numValue);
    }
  };
  
  const lrvValue = toNumberOrNull(localValue) || toNumberOrNull(value);
  const lowLRV = isLowLRV(lrvValue);
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="relative">
            <Input
              type="number"
              min="0"
              max="100"
              step="1"
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              onBlur={handleBlur}
              disabled={disabled}
              className={`h-8 w-16 text-center text-sm ${
                lrvValue === null ? 'border-slate-300' :
                lowLRV ? 'border-amber-300 dark:border-amber-700' :
                'border-green-300 dark:border-green-700'
              }`}
              placeholder="—"
            />
            {source && source !== 'manual' && localValue === '' && (
              <HelpCircle className="absolute -right-5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            {source === 'manual' ? 'Manual entry' : 
             source === 'color' ? 'From color dataset (enter to override)' : 
             'Enter LRV value (0-100)'}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// ============================================================================
// EXPANDABLE AREA DETAILS COMPONENT
// ============================================================================

const ExpandableAreaDetails = ({ area, onUpdate, disabled }) => {
  const [localValues, setLocalValues] = useState({
    scope_of_work_text: area.scope_of_work_text || '',
    paint_areas_text: area.paint_areas_text || '',
    repair_areas_text: area.repair_areas_text || ''
  });
  const [hasChanges, setHasChanges] = useState(false);
  
  useEffect(() => {
    setLocalValues({
      scope_of_work_text: area.scope_of_work_text || '',
      paint_areas_text: area.paint_areas_text || '',
      repair_areas_text: area.repair_areas_text || ''
    });
    setHasChanges(false);
  }, [area.scope_of_work_text, area.paint_areas_text, area.repair_areas_text]);
  
  const handleChange = (field, value) => {
    setLocalValues(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };
  
  const handleBlur = (field) => {
    if (hasChanges) {
      onUpdate(area.area_key, { [field]: localValues[field]?.trim() || null });
    }
  };
  
  const getCharCount = (text, max = 1000) => {
    const count = (text || '').length;
    return { count, remaining: max - count, isOver: count > max };
  };
  
  return (
    <div className="col-span-12 bg-slate-50/50 dark:bg-slate-800/30 rounded-lg p-4 mt-2 mb-1 border border-slate-200 dark:border-slate-700">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-medium text-slate-600 dark:text-slate-400">
              Scope of Work
            </Label>
            <span className={`text-xs ${getCharCount(localValues.scope_of_work_text).isOver ? 'text-red-500' : 'text-slate-400'}`}>
              {getCharCount(localValues.scope_of_work_text).count}/1000
            </span>
          </div>
          <Textarea
            value={localValues.scope_of_work_text}
            onChange={(e) => handleChange('scope_of_work_text', e.target.value)}
            onBlur={() => handleBlur('scope_of_work_text')}
            placeholder="Prep + mask, prime, apply RhinoShield topcoat, detail cut-ins…"
            className="min-h-[100px] text-sm resize-none"
            disabled={disabled}
            maxLength={1000}
          />
        </div>
        
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-medium text-slate-600 dark:text-slate-400">
              Areas to be Painted
            </Label>
            <span className={`text-xs ${getCharCount(localValues.paint_areas_text).isOver ? 'text-red-500' : 'text-slate-400'}`}>
              {getCharCount(localValues.paint_areas_text).count}/1000
            </span>
          </div>
          <Textarea
            value={localValues.paint_areas_text}
            onChange={(e) => handleChange('paint_areas_text', e.target.value)}
            onBlur={() => handleBlur('paint_areas_text')}
            placeholder="Garage door panels, frame, exterior jamb, trim boards…"
            className="min-h-[100px] text-sm resize-none"
            disabled={disabled}
            maxLength={1000}
          />
        </div>
        
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-medium text-slate-600 dark:text-slate-400">
              Areas Needing Repair
            </Label>
            <span className={`text-xs ${getCharCount(localValues.repair_areas_text).isOver ? 'text-red-500' : 'text-slate-400'}`}>
              {getCharCount(localValues.repair_areas_text).count}/1000
            </span>
          </div>
          <Textarea
            value={localValues.repair_areas_text}
            onChange={(e) => handleChange('repair_areas_text', e.target.value)}
            onBlur={() => handleBlur('repair_areas_text')}
            placeholder="Fill cracks at corners, repair wood rot at bottom trim, patch stucco by entry…"
            className="min-h-[100px] text-sm resize-none"
            disabled={disabled}
            maxLength={1000}
          />
        </div>
      </div>
      
      {hasChanges && (
        <div className="mt-2 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <Info className="h-3 w-3" />
          Unsaved changes - click outside to save
        </div>
      )}
    </div>
  );
};

// ============================================================================
// LRV WARNING MODAL (Simple warning - not a blocker)
// ============================================================================

const LRVWarningModal = ({ isOpen, onClose, onConfirm, colorName, lrvValue }) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  
  const handleConfirm = () => {
    if (dontShowAgain) {
      localStorage.setItem('lrv_warning_dismissed', 'true');
    }
    onConfirm();
    onClose();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Low LRV Color Selected
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
              {colorName || 'This color'} has an LRV of {lrvValue}
            </p>
          </div>
          
          <div className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
            <p className="font-medium text-slate-900 dark:text-slate-100">How low LRV affects main areas:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Darker colors absorb more heat, which can cause expansion and contraction</li>
              <li>May show imperfections and surface texture more visibly</li>
              <li>Can fade faster when exposed to direct sunlight</li>
              <li>May require more coats for even coverage</li>
              <li>Higher maintenance in high-traffic or sun-exposed areas</li>
            </ul>
          </div>
          
          <div className="flex items-center space-x-2 pt-2">
            <Checkbox 
              id="dontShowAgain" 
              checked={dontShowAgain}
              onCheckedChange={setDontShowAgain}
            />
            <label 
              htmlFor="dontShowAgain" 
              className="text-sm text-slate-600 dark:text-slate-400 cursor-pointer"
            >
              Don't show this warning again
            </label>
          </div>
        </div>
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>
            OK - Use This Color
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// CUSTOM COLOR FORM COMPONENT (Full Production Details)
// ============================================================================

const CustomColorForm = ({ onAddColorWithArea, areaLabels, existingAreas, disabled }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [formData, setFormData] = useState({
    // Color details
    brand: '',
    color_name: '',
    color_code: '',
    hex_code: '',
    // Area assignment
    area_key: '',
    // Production details
    repair_areas_text: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Get available areas (those that exist in the scope)
  const availableAreas = Object.entries(areaLabels || {}).map(([key, label]) => ({
    key,
    label,
    hasColor: existingAreas?.some(a => a.area_key === key && a.paint_color_id)
  }));
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.brand || !formData.color_name || !formData.color_code) {
      toast.error('Please fill in Brand, Name, and Color Code');
      return;
    }
    
    if (!formData.area_key) {
      toast.error('Please select an area to paint');
      return;
    }
    
    setSaving(true);
    try {
      await onAddColorWithArea({
        // Color data
        brand: formData.brand.trim(),
        color_name: formData.color_name.trim(),
        color_number: formData.color_code.trim(),
        hex_code: formData.hex_code.trim() || null,
        is_custom: true,
        // Area assignment
        area_key: formData.area_key,
        // Production details
        repair_areas_text: formData.repair_areas_text.trim() || null,
        notes: formData.notes.trim() || null
      });
      
      // Reset form
      setFormData({
        brand: '',
        color_name: '',
        color_code: '',
        hex_code: '',
        area_key: '',
        repair_areas_text: '',
        notes: ''
      });
      toast.success('Custom color added and assigned to area');
    } catch (error) {
      console.error('Error adding custom color:', error);
      toast.error('Failed to add custom color');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <Card className="mt-4 border-blue-200 dark:border-blue-800">
      <CardHeader className="pb-2 bg-blue-50/50 dark:bg-blue-900/10">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center justify-between w-full text-left"
          disabled={disabled}
        >
          <div className="flex items-center gap-2">
            <Plus className="h-4 w-4 text-blue-500" />
            <span className="font-medium text-sm">Add Custom Color for Production</span>
          </div>
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-slate-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-slate-500" />
          )}
        </button>
        <p className="text-xs text-slate-500 mt-1">
          Add colors not in the list and assign to an area with production details
        </p>
      </CardHeader>
      
      {isExpanded && (
        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Color Details Section */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                <Paintbrush className="h-4 w-4 text-blue-500" />
                Color Details
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pl-6">
                <div className="space-y-1.5">
                  <Label className="text-xs">Brand *</Label>
                  <Input
                    value={formData.brand}
                    onChange={(e) => setFormData(prev => ({ ...prev, brand: e.target.value }))}
                    placeholder="e.g., Benjamin Moore, PPG"
                    className="h-9"
                    disabled={disabled || saving}
                  />
              </div>
              
              <div className="space-y-1.5">
                <Label className="text-xs">Color Name *</Label>
                <Input
                  value={formData.color_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, color_name: e.target.value }))}
                  placeholder="e.g., Simply White"
                  className="h-9"
                  disabled={disabled || saving}
                />
              </div>
              
              <div className="space-y-1.5">
                <Label className="text-xs">Color Code *</Label>
                <Input
                  value={formData.color_code}
                  onChange={(e) => setFormData(prev => ({ ...prev, color_code: e.target.value }))}
                  placeholder="e.g., OC-117"
                  className="h-9"
                  disabled={disabled || saving}
                />
              </div>
              
              <div className="space-y-1.5">
                <Label className="text-xs">Hex Code (optional)</Label>
                <div className="flex gap-2">
                  <Input
                    value={formData.hex_code}
                    onChange={(e) => setFormData(prev => ({ ...prev, hex_code: e.target.value }))}
                    placeholder="#FFFFFF"
                    className="h-9 flex-1"
                    disabled={disabled || saving}
                  />
                  {formData.hex_code && (
                    <ColorSwatch hexCode={formData.hex_code} size="md" />
                  )}
                </div>
              </div>
              </div>
            </div>
            
            {/* Area Assignment Section */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                <Info className="h-4 w-4 text-blue-500" />
                Area Assignment
              </h4>
              <div className="pl-6">
                <div className="space-y-1.5">
                  <Label className="text-xs">Area to be Painted *</Label>
                  <select
                    value={formData.area_key}
                    onChange={(e) => setFormData(prev => ({ ...prev, area_key: e.target.value }))}
                    className="w-full h-9 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm"
                    disabled={disabled || saving}
                  >
                    <option value="">Select area...</option>
                    {availableAreas.map(area => (
                      <option key={area.key} value={area.key}>
                        {area.label} {area.hasColor ? '(has color)' : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500">
                    This color will be assigned to the selected area
                  </p>
                </div>
              </div>
            </div>
            
            {/* Production Details Section */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Production Details
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                <div className="space-y-1.5">
                  <Label className="text-xs">Areas Needing Repair</Label>
                  <Textarea
                    value={formData.repair_areas_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, repair_areas_text: e.target.value }))}
                    placeholder="e.g., Fill cracks at corners, repair wood rot at bottom trim, patch stucco by entry..."
                    className="min-h-[80px] text-sm resize-none"
                    disabled={disabled || saving}
                    maxLength={1000}
                  />
                  <p className="text-xs text-slate-400">Describe any repairs needed before painting</p>
                </div>
                
                <div className="space-y-1.5">
                  <Label className="text-xs">Notes to Crew</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="e.g., Customer requests 2 coats, avoid spraying near garden, use ladder on south side..."
                    className="min-h-[80px] text-sm resize-none"
                    disabled={disabled || saving}
                    maxLength={1000}
                  />
                  <p className="text-xs text-slate-400">Special instructions for the production crew</p>
                </div>
              </div>
            </div>
            
            {/* Submit Button */}
            <div className="flex justify-end pt-2 border-t border-slate-200 dark:border-slate-700">
              <Button type="submit" disabled={disabled || saving}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Color & Assign to Area
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      )}
    </Card>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ScopeOfWorkTab = ({ project, user, onRefresh }) => {
  const [scope, setScope] = useState(null);
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [warningModal, setWarningModal] = useState({ open: false, colorName: null, lrvValue: null, pendingUpdate: null });
  const [expandedRows, setExpandedRows] = useState({});
  
  const toggleRowExpansion = useCallback((areaKey) => {
    setExpandedRows(prev => ({
      ...prev,
      [areaKey]: !prev[areaKey]
    }));
  }, []);
  
  const colorMap = useMemo(() => {
    const map = {};
    colors.forEach(c => { map[c.id] = c; });
    return map;
  }, [colors]);
  
  // Fetch colors and scope data
  useEffect(() => {
    const fetchData = async () => {
      if (!project?.id) return;
      
      try {
        const [colorsRes, scopeRes] = await Promise.all([
          axios.get(`${API}/paint-colors?limit=500`),
          axios.get(`${API}/jobs/${project.id}/scope-of-work`)
        ]);
        
        setColors(colorsRes.data);
        setScope(scopeRes.data);
      } catch (error) {
        console.error('Error fetching scope data:', error);
        toast.error('Failed to load scope of work');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [project?.id]);
  
  // Check if user can edit
  const canEdit = useMemo(() => {
    const role = user?.role || '';
    return ['super_admin', 'admin', 'sales', 'sales_rep', 'sales_manager', 
            'director_of_sales', 'ops', 'operations', 'ops_manager', 'operations_manager',
            'foreman', 'project_manager'].includes(role);
  }, [user?.role]);
  
  // Calculate effective LRV for each area
  const areasWithEffectiveLRV = useMemo(() => {
    if (!scope?.areas) return [];
    
    return scope.areas.map(area => {
      const selectedColor = area.paint_color || (area.paint_color_id ? colorMap[area.paint_color_id] : null);
      const { value: effectiveLRV, source } = getEffectiveLRV(area, selectedColor);
      
      return {
        ...area,
        effectiveLRV,
        lrvSource: source,
        selectedColor
      };
    });
  }, [scope?.areas, colorMap]);
  
  // Check if LRV warning should be shown
  const shouldShowLRVWarning = useCallback(() => {
    return localStorage.getItem('lrv_warning_dismissed') !== 'true';
  }, []);
  
  // Handle color selection
  const handleColorSelect = useCallback(async (areaKey, color) => {
    if (!canEdit || !project?.id) return;
    
    const colorLRV = toNumberOrNull(color.lrv);
    
    // Check if LRV is low and warning hasn't been dismissed
    if (isLowLRV(colorLRV) && shouldShowLRVWarning()) {
      setWarningModal({
        open: true,
        colorName: color.color_name,
        lrvValue: colorLRV,
        pendingUpdate: { areaKey, color }
      });
      return;
    }
    
    // Proceed with update
    await performColorUpdate(areaKey, color);
  }, [canEdit, project?.id, shouldShowLRVWarning]);
  
  // Perform the actual color update
  const performColorUpdate = useCallback(async (areaKey, color) => {
    setSaving(true);
    try {
      const colorLRV = toNumberOrNull(color.lrv);
      const areaUpdate = {
        area_key: areaKey,
        paint_color_id: color.id,
        color_name: color.color_name,
        color_number: color.color_number,
        hex_code: color.hex_code,
        lrv_value: colorLRV
      };
      
      await axios.put(`${API}/jobs/${project.id}/scope-of-work`, [areaUpdate]);
      
      const scopeRes = await axios.get(`${API}/jobs/${project.id}/scope-of-work`);
      setScope(scopeRes.data);
      
      toast.success('Color updated');
    } catch (error) {
      console.error('Error updating color:', error);
      toast.error('Failed to update color');
    } finally {
      setSaving(false);
    }
  }, [project?.id]);
  
  // Handle warning modal confirmation
  const handleWarningConfirm = useCallback(async () => {
    if (warningModal.pendingUpdate) {
      const { areaKey, color } = warningModal.pendingUpdate;
      await performColorUpdate(areaKey, color);
    }
  }, [warningModal.pendingUpdate, performColorUpdate]);
  
  // Handle manual LRV update
  const handleLRVUpdate = useCallback(async (areaKey, lrvValue) => {
    if (!canEdit || !project?.id) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/jobs/${project.id}/scope-of-work`, [{
        area_key: areaKey,
        lrv_value: lrvValue
      }]);
      
      const scopeRes = await axios.get(`${API}/jobs/${project.id}/scope-of-work`);
      setScope(scopeRes.data);
      
      toast.success('LRV updated');
    } catch (error) {
      console.error('Error updating LRV:', error);
      toast.error('Failed to update LRV');
    } finally {
      setSaving(false);
    }
  }, [canEdit, project?.id]);
  
  // Handle notes update
  const handleNotesUpdate = useCallback(async (areaKey, notes) => {
    if (!canEdit || !project?.id) return;
    
    try {
      await axios.put(`${API}/jobs/${project.id}/scope-of-work`, [{
        area_key: areaKey,
        notes
      }]);
    } catch (error) {
      console.error('Error updating notes:', error);
    }
  }, [canEdit, project?.id]);
  
  // Handle expanded details update
  const handleDetailsUpdate = useCallback(async (areaKey, updates) => {
    if (!canEdit || !project?.id) return;
    
    try {
      setScope(prev => ({
        ...prev,
        areas: prev.areas.map(a => 
          a.area_key === areaKey ? { ...a, ...updates } : a
        )
      }));
      
      await axios.put(`${API}/jobs/${project.id}/scope-of-work`, [{
        area_key: areaKey,
        ...updates
      }]);
      
      toast.success('Details saved');
    } catch (error) {
      console.error('Error updating details:', error);
      toast.error('Failed to save details');
      const scopeRes = await axios.get(`${API}/jobs/${project.id}/scope-of-work`);
      setScope(scopeRes.data);
    }
  }, [canEdit, project?.id]);
  
  // Handle adding custom color
  const handleAddCustomColor = useCallback(async (colorData) => {
    try {
      const response = await axios.post(`${API}/paint-colors/custom`, colorData);
      
      // Add the new color to the local colors list
      setColors(prev => [...prev, response.data]);
      
      return response.data;
    } catch (error) {
      console.error('Error adding custom color:', error);
      throw error;
    }
  }, []);
  
  // Handle adding custom color with area assignment and production details
  const handleAddCustomColorWithArea = useCallback(async (formData) => {
    if (!canEdit || !project?.id) return;
    
    try {
      // First, create the custom color
      const colorData = {
        brand: formData.brand,
        color_name: formData.color_name,
        color_number: formData.color_number,
        hex_code: formData.hex_code,
        is_custom: true
      };
      
      const colorResponse = await axios.post(`${API}/paint-colors/custom`, colorData);
      const newColor = colorResponse.data;
      
      // Add the new color to the local colors list
      setColors(prev => [...prev, newColor]);
      
      // Then assign it to the area with production details
      const areaUpdate = {
        area_key: formData.area_key,
        paint_color_id: newColor.id,
        color_name: newColor.color_name,
        color_number: newColor.color_number,
        hex_code: newColor.hex_code,
        repair_areas_text: formData.repair_areas_text,
        notes: formData.notes
      };
      
      await axios.put(`${API}/jobs/${project.id}/scope-of-work`, [areaUpdate]);
      
      // Refresh the scope data
      const scopeRes = await axios.get(`${API}/jobs/${project.id}/scope-of-work`);
      setScope(scopeRes.data);
      
      return newColor;
    } catch (error) {
      console.error('Error adding custom color with area:', error);
      throw error;
    }
  }, [canEdit, project?.id]);
  
  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        </CardContent>
      </Card>
    );
  }
  
  if (!project) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-slate-500">No project selected</p>
        </CardContent>
      </Card>
    );
  }
  
  const areaLabels = scope?.area_labels || {};
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold">Scope of Work - Paint Colors</h3>
          {saving && (
            <span className="flex items-center gap-1.5 text-sm text-blue-600">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Saving...
            </span>
          )}
        </div>
      </div>
      
      {/* Areas Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="grid grid-cols-12 gap-4 text-xs font-medium text-slate-500 uppercase tracking-wider pl-8">
            <div className="col-span-2">Area</div>
            <div className="col-span-1">Swatch</div>
            <div className="col-span-3">Color</div>
            <div className="col-span-1 text-center">LRV</div>
            <div className="col-span-5">Notes</div>
          </div>
        </CardHeader>
        <CardContent className="space-y-1">
          {areasWithEffectiveLRV.map((area) => {
            const isExpanded = expandedRows[area.area_key];
            const hasDetails = area.scope_of_work_text || area.paint_areas_text || area.repair_areas_text;
            const lowLRV = isLowLRV(area.effectiveLRV);
            
            return (
              <div 
                key={area.area_key}
                className={`rounded-lg transition-colors ${isExpanded ? 'bg-slate-50/50 dark:bg-slate-800/20' : ''}`}
              >
                {/* Main Row */}
                <div className="grid grid-cols-12 gap-4 items-center py-3 border-b border-slate-100 dark:border-slate-800">
                  <div className="col-span-12 md:col-span-12 flex items-center">
                    <button
                      onClick={() => toggleRowExpansion(area.area_key)}
                      className="p-1.5 mr-2 rounded-md hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors border border-slate-200 dark:border-slate-600"
                      title={isExpanded ? 'Collapse details' : 'Expand details'}
                      data-testid={`expand-${area.area_key}`}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-slate-600 dark:text-slate-300" />
                      ) : (
                        <ChevronRight className={`h-4 w-4 ${hasDetails ? 'text-blue-500' : 'text-slate-500 dark:text-slate-400'}`} />
                      )}
                    </button>
                    
                    <div className="grid grid-cols-12 gap-4 items-center flex-1">
                      {/* Area Name */}
                      <div className="col-span-2">
                        <button 
                          onClick={() => toggleRowExpansion(area.area_key)}
                          className="text-left hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                        >
                          <p className="font-medium text-sm">{areaLabels[area.area_key] || area.area_key}</p>
                          {hasDetails && !isExpanded && (
                            <p className="text-xs text-blue-500 mt-0.5">Has details ▸</p>
                          )}
                        </button>
                      </div>
                      
                      {/* Color Swatch */}
                      <div className="col-span-1">
                        <ColorSwatch hexCode={area.hex_code} size="md" />
                      </div>
                      
                      {/* Color Dropdown */}
                      <div className="col-span-3">
                        <ColorSearchDropdown
                          key={`${area.area_key}-${area.paint_color_id || 'none'}`}
                          value={area.paint_color_id}
                          onChange={(color) => handleColorSelect(area.area_key, color)}
                          colors={colors}
                          disabled={!canEdit}
                          currentColor={area.color_name ? {
                            id: area.paint_color_id,
                            color_name: area.color_name,
                            color_number: area.color_number,
                            hex_code: area.hex_code,
                            lrv: area.lrv_value
                          } : null}
                        />
                      </div>
                      
                      {/* LRV Value */}
                      <div className="col-span-1 flex justify-center">
                        <LRVInput
                          value={area.lrv_value}
                          onChange={(val) => handleLRVUpdate(area.area_key, val)}
                          disabled={!canEdit}
                          source={area.lrvSource}
                        />
                      </div>
                      
                      {/* Notes */}
                      <div className="col-span-5">
                        <Input
                          value={area.notes || ''}
                          onChange={(e) => {
                            setScope(prev => ({
                              ...prev,
                              areas: prev.areas.map(a => 
                                a.area_key === area.area_key ? { ...a, notes: e.target.value } : a
                              )
                            }));
                          }}
                          onBlur={(e) => handleNotesUpdate(area.area_key, e.target.value)}
                          placeholder="Add notes..."
                          className="h-8 text-sm"
                          disabled={!canEdit}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Expandable Details Panel */}
                {isExpanded && (
                  <ExpandableAreaDetails
                    area={area}
                    onUpdate={handleDetailsUpdate}
                    disabled={!canEdit}
                  />
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
      
      {/* Custom Color Form */}
      <CustomColorForm 
        onAddColorWithArea={handleAddCustomColorWithArea}
        areaLabels={areaLabels}
        existingAreas={areasWithEffectiveLRV}
        disabled={!canEdit}
      />
      
      {/* LRV Warning Modal */}
      <LRVWarningModal
        isOpen={warningModal.open}
        onClose={() => setWarningModal({ open: false, colorName: null, lrvValue: null, pendingUpdate: null })}
        onConfirm={handleWarningConfirm}
        colorName={warningModal.colorName}
        lrvValue={warningModal.lrvValue}
      />
    </div>
  );
};

export default ScopeOfWorkTab;
